import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Permissions as P, type VoiceState } from '@comms/shared';
import { Track, type Participant, type TrackPublication } from 'livekit-client';
import { useShallow } from 'zustand/react/shallow';
import { displayName, useStore } from '../store';
import { openModal } from '../store/ui';
import { has, usePermissions } from '../hooks/permissions';
import {
  clearVoiceError,
  enableAudio,
  joinVoice,
  leaveVoice,
  toggleCamera,
  toggleDeafen,
  toggleMute,
  useVoice,
  watchStream,
} from '../lib/voice';
import { Avatar, Button, Icon, cx } from './ui';
import { ScreenShareButton } from './GoLive';
import { VideoStage, useHasVideo } from './VideoStage';
import { dmTitle } from './DmSidebar';
import { NavButton } from './MobileNav';
import { nativeCall, nextRoute, useNativeAudio, type AudioRoute } from '../lib/native';

const NO_SPEAKERS: string[] = [];

export function useChannelVoiceStates(channelId: string): VoiceState[] {
  return useStore(
    useShallow((s) => Object.values(s.voiceStates).filter((v) => v.channelId === channelId)),
  );
}

/** Participant list shown under a voice channel in the sidebar. */
export function VoiceParticipants({ channelId, guildId }: { channelId: string; guildId: string }) {
  const states = useChannelVoiceStates(channelId);
  const speaking = useVoice((s) => (s.channelId === channelId ? s.speaking : NO_SPEAKERS));
  const here = useVoice((s) => s.channelId === channelId && s.status === 'connected');
  const meId = useStore((s) => s.me?.id);
  const navigate = useNavigate();
  if (states.length === 0) return null;
  return (
    <div className="mb-1 ml-9 space-y-0.5">
      {states.map((v) => (
        <div key={v.userId} className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => openModal({ kind: 'profile', userId: v.userId, guildId })}
            className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-0.5 text-left text-sm text-text-3 hover:bg-bg-hover hover:text-text-1"
          >
            <span
              className={cx('rounded-full', speaking.includes(v.userId) && 'ring-2 ring-success')}
            >
              <Avatar userId={v.userId} size={22} />
            </span>
            <span className="flex-1 truncate">
              {displayName(useStore.getState(), v.userId, guildId)}
            </span>
            <VoiceStateIcons
              v={{ ...v, selfStream: v.selfStream && !(here && v.userId !== meId) }}
            />
          </button>
          {v.selfStream && here && v.userId !== meId && (
            <button
              type="button"
              title="Watch stream"
              onClick={() => {
                watchStream(v.userId);
                navigate(`/channels/${guildId}/${channelId}`);
              }}
              className="rounded bg-danger px-1 text-[9px] font-bold text-white hover:brightness-110"
            >
              LIVE
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function VoiceStateIcons({ v }: { v: VoiceState }) {
  return (
    <span className="flex items-center gap-0.5 text-text-3">
      {v.selfStream && (
        <span className="rounded bg-danger px-1 text-[9px] font-bold text-white">LIVE</span>
      )}
      {v.selfVideo && <Icon name="video" className="h-3.5 w-3.5" />}
      {(v.serverMute || v.selfMute) && (
        <Icon name="micOff" className={cx('h-3.5 w-3.5', v.serverMute && 'text-danger')} />
      )}
      {(v.serverDeaf || v.selfDeaf) && (
        <Icon name="headphonesOff" className={cx('h-3.5 w-3.5', v.serverDeaf && 'text-danger')} />
      )}
    </span>
  );
}

/** "Voice Connected" bar above the user panel. */
/** Problems that leave a connected call silent or blank, with what to do about them. */
export function CallWarnings() {
  const { audioBlocked, decryptProblem, keysUnavailable } = useVoice(
    useShallow((s) => ({
      audioBlocked: s.audioBlocked,
      decryptProblem: s.decryptProblem,
      keysUnavailable: s.keysUnavailable,
    })),
  );
  if (!audioBlocked && !decryptProblem) return null;
  return (
    <div role="status" className="space-y-1 text-xs">
      {audioBlocked && (
        <button
          type="button"
          onClick={() => void enableAudio()}
          className="w-full rounded bg-warning px-2 py-1 font-semibold text-black"
        >
          Your browser paused the sound. Click to hear the call
        </button>
      )}
      {decryptProblem && (
        <div className="rounded bg-danger/20 px-2 py-1 text-text-0">
          {keysUnavailable
            ? "Can't decrypt the call: this device lost the conversation's encryption keys. Leave the call, close and reopen the app, then call again."
            : "Can't decrypt someone's audio or video: the call's encryption keys are out of sync. Trying to fix it… If it doesn't clear, both of you should fully close and reopen the app (an out-of-date copy can't keep up), then call again."}
        </div>
      )}
    </div>
  );
}

export function VoicePanel() {
  const { status, channelId, guildId, error, kind, encrypted } = useVoice();
  const channel = useStore((s) => (channelId ? s.channels[channelId] : undefined));
  const dmName = useStore((s) => (kind === 'dm' && channel ? dmTitle(s, channel) : ''));
  const guild = useStore((s) => (guildId ? s.guilds[guildId] : undefined));
  const navigate = useNavigate();
  if (status === 'idle' && !error) return null;
  return (
    <div className="border-b border-bg-3 bg-bg-0/60 px-3 py-2">
      {error && (
        <div className="mb-1 flex items-start gap-1 text-xs text-danger">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={clearVoiceError} aria-label="Dismiss">
            <Icon name="x" className="h-3 w-3" />
          </button>
        </div>
      )}
      {/* DM calls show these in the call itself. */}
      {status !== 'idle' && kind === 'guild' && <CallWarnings />}
      {status !== 'idle' && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="min-w-0 flex-1 text-left"
            onClick={() => channelId && navigate(`/channels/${guildId ?? '@me'}/${channelId}`)}
          >
            <div
              className={cx(
                'text-sm font-semibold',
                status === 'connected' ? 'text-success' : 'text-warning',
              )}
            >
              {status === 'connected'
                ? kind === 'dm'
                  ? 'Call Connected'
                  : 'Voice Connected'
                : status === 'reconnecting'
                  ? 'Reconnecting…'
                  : 'Connecting…'}
            </div>
            <div className="flex items-center gap-1 truncate text-xs text-text-3">
              {kind === 'dm' ? (
                <>
                  {encrypted && <Icon name="lock" className="h-3 w-3 shrink-0 text-success" />}
                  <span className="truncate">{dmName}</span>
                </>
              ) : (
                <>
                  {channel?.name} / {guild?.name}
                </>
              )}
            </div>
          </button>
          <AudioOutputButton compact />
          <button
            type="button"
            title="Disconnect"
            onClick={() => void leaveVoice()}
            className="rounded p-1.5 text-text-2 hover:bg-bg-hover hover:text-danger"
          >
            <Icon name="phoneOff" />
          </button>
        </div>
      )}
    </div>
  );
}

const ROUTE_LABEL: Record<AudioRoute, string> = {
  earpiece: 'Earpiece',
  speaker: 'Speaker',
  headset: 'Headset',
  bluetooth: 'Bluetooth',
};
const ROUTE_ICON: Record<AudioRoute, 'phone' | 'volume' | 'headphones'> = {
  earpiece: 'phone',
  speaker: 'volume',
  headset: 'headphones',
  bluetooth: 'headphones',
};

/**
 * Android app: where the call plays (earpiece, speaker, headset, Bluetooth). Tap to switch
 * to the next one. Only shown when the app reports more than one.
 */
export function AudioOutputButton({ compact }: { compact?: boolean }) {
  const { available, current } = useNativeAudio();
  const next = nextRoute(available, current);
  if (!nativeCall.available() || !current || !next) return null;
  const title = `Audio: ${ROUTE_LABEL[current]}. Switch to ${ROUTE_LABEL[next]}`;
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      data-route={current}
      onClick={() => nativeCall.route(next)}
      className={
        compact
          ? 'rounded p-1.5 text-text-2 hover:bg-bg-hover hover:text-text-0'
          : 'flex items-center gap-1 rounded-full bg-bg-3 p-3 text-text-0 hover:bg-bg-active'
      }
    >
      <Icon name={ROUTE_ICON[current]} />
      {!compact && <span className="text-xs">{ROUTE_LABEL[current]}</span>}
    </button>
  );
}

/** Mute / deafen buttons that live in the user panel (work before joining, too). */
export function VoiceToggles() {
  const { muted, deafened } = useVoice();
  const meId = useStore((s) => s.me?.id);
  const serverMuted = useStore((s) => (meId ? !!s.voiceStates[meId]?.serverMute : false));
  return (
    <>
      <button
        type="button"
        title={serverMuted ? 'Server muted by a moderator' : muted ? 'Unmute' : 'Mute'}
        onClick={() => void toggleMute()}
        className={cx(
          'rounded p-1.5 hover:bg-bg-hover',
          muted || serverMuted ? 'text-danger' : 'text-text-2 hover:text-text-0',
        )}
      >
        <Icon name={muted || serverMuted ? 'micOff' : 'mic'} />
      </button>
      <button
        type="button"
        title={deafened ? 'Undeafen' : 'Deafen'}
        onClick={() => void toggleDeafen()}
        className={cx(
          'rounded p-1.5 hover:bg-bg-hover',
          deafened ? 'text-danger' : 'text-text-2 hover:text-text-0',
        )}
      >
        <Icon name={deafened ? 'headphonesOff' : 'headphones'} />
      </button>
    </>
  );
}

export function VideoTile({
  pub,
  label,
  mirror,
}: {
  pub: TrackPublication;
  label: string;
  mirror?: boolean;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const track = pub.track;
    const el = ref.current;
    if (!track || !el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [pub, pub.track]);
  return (
    <div className="relative min-h-0 overflow-hidden rounded-lg bg-black">
      <video
        ref={ref}
        autoPlay
        playsInline
        muted
        className={cx('h-full w-full object-contain', mirror && '-scale-x-100')}
      />
      <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
        {label}
      </span>
    </div>
  );
}

export function videoPublications(p: Participant): TrackPublication[] {
  return [...p.trackPublications.values()].filter(
    (pub) =>
      pub.kind === Track.Kind.Video &&
      pub.track &&
      !pub.isMuted &&
      (pub.source === Track.Source.Camera || pub.source === Track.Source.ScreenShare),
  );
}

/** Main area for a voice channel: participants grid, video, and call controls. */
export function VoiceChannelView({ guildId, channelId }: { guildId: string; channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const states = useChannelVoiceStates(channelId);
  const voice = useVoice();
  const perms = usePermissions(guildId, channelId);
  const enabled = useStore((s) => s.voiceEnabled);
  const here = voice.channelId === channelId && voice.status !== 'idle';
  void voice.tracksVersion; // re-render on track changes

  const hasVideo = useHasVideo(guildId) || (here && voice.screen);

  if (!channel) return null;
  const full =
    channel.userLimit > 0 && states.length >= channel.userLimit && !has(perms, P.MOVE_MEMBERS);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-black/90">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-black px-4 text-white">
        <NavButton />
        <Icon name="speaker" className="h-5 w-5 text-text-3" />
        <h1 className="font-semibold">{channel.name}</h1>
        {channel.userLimit > 0 && (
          <span className="text-xs text-text-3">
            {states.length}/{channel.userLimit}
          </span>
        )}
      </header>
      <div
        className={cx(
          'flex min-h-0 flex-1 flex-col items-center gap-4 overflow-hidden p-3 md:p-6',
          !hasVideo && 'justify-center',
        )}
      >
        {here && <VideoStage guildId={guildId} states={states} />}
        <div className="flex shrink-0 flex-wrap justify-center gap-4">
          {states.map((v) => (
            <div
              key={v.userId}
              className={cx(
                'flex flex-col items-center gap-2 rounded-lg bg-bg-1',
                hasVideo ? 'w-28 p-2' : 'w-36 p-4',
                here && voice.speaking.includes(v.userId) && 'ring-2 ring-success',
              )}
            >
              <Avatar userId={v.userId} size={hasVideo ? 36 : 64} />
              <span className="flex max-w-full items-center gap-1 text-sm text-text-0">
                <span className="truncate">
                  {displayName(useStore.getState(), v.userId, guildId)}
                </span>
                <span className="shrink-0">
                  <VoiceStateIcons v={v} />
                </span>
              </span>
            </div>
          ))}
        </div>
        {states.length === 0 && !here && <p className="text-text-3">Nobody is here yet.</p>}
      </div>
      <div className="flex flex-wrap justify-center gap-3 p-4">
        {!enabled ? (
          <p className="text-sm text-text-3">
            Voice isn't configured on this server yet. Ask the admin to set up LiveKit.
          </p>
        ) : !here ? (
          <Button
            onClick={() => void joinVoice(guildId, channelId)}
            disabled={!has(perms, P.CONNECT) || full}
            className="bg-success hover:bg-success"
          >
            {full ? 'Channel full' : has(perms, P.CONNECT) ? 'Join Voice' : 'No permission to join'}
          </Button>
        ) : (
          <>
            <CallButton
              title={voice.muted ? 'Unmute' : 'Mute'}
              active={voice.muted}
              icon={voice.muted ? 'micOff' : 'mic'}
              onClick={toggleMute}
            />
            <CallButton
              title={voice.deafened ? 'Undeafen' : 'Deafen'}
              active={voice.deafened}
              icon={voice.deafened ? 'headphonesOff' : 'headphones'}
              onClick={toggleDeafen}
            />
            {has(perms, P.STREAM) && (
              <>
                <CallButton
                  title={voice.camera ? 'Turn off camera' : 'Turn on camera'}
                  on={voice.camera}
                  icon="video"
                  onClick={toggleCamera}
                />
                <ScreenShareButton />
              </>
            )}
            <button
              type="button"
              title="Disconnect"
              onClick={() => void leaveVoice()}
              className="rounded-full bg-danger p-3 text-white hover:brightness-110"
            >
              <Icon name="phoneOff" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function CallButton({
  title,
  icon,
  onClick,
  active,
  on,
}: {
  title: string;
  icon: 'mic' | 'micOff' | 'headphones' | 'headphonesOff' | 'video' | 'monitor';
  onClick: () => Promise<void>;
  active?: boolean;
  on?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={!!(active || on)}
      onClick={() => void onClick()}
      className={cx(
        'rounded-full p-3',
        active
          ? 'bg-white text-danger'
          : on
            ? 'bg-success text-white'
            : 'bg-bg-3 text-text-0 hover:bg-bg-active',
      )}
    >
      <Icon name={icon} />
    </button>
  );
}
